import { Box, Button, Flex, HStack, IconButton, Portal, Text } from "@chakra-ui/react";
import { useEffect, useRef, type KeyboardEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { FiChevronLeft, FiChevronRight, FiX } from "react-icons/fi";
import { altKeyLabel, isExactlyAlt } from "../platform";
import { useHiddenIntercomLauncher } from "./useHiddenIntercomLauncher";

export interface FocusOverlayPosition {
  current: number;
  total: number;
}

interface Props {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  position?: FocusOverlayPosition;
  onPrevious?: () => void;
  onNext?: () => void;
  hasPrevious?: boolean;
  hasNext?: boolean;
  previousLabel?: string;
  nextLabel?: string;
  onClose: () => void;
  listPanel?: ReactNode;
  footerStart?: ReactNode;
  footerEnd?: ReactNode;
  scrollResetKey?: string;
  children: ReactNode;
}

// In a field the OS owns Alt+arrow: it moves the caret a word. The justification keeps that.
const isTextEntry = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  (target.tagName === "TEXTAREA" || target.tagName === "INPUT" || target.isContentEditable);

export const FocusOverlay = ({
  eyebrow,
  title,
  subtitle,
  position,
  onPrevious,
  onNext,
  hasPrevious = false,
  hasNext = false,
  previousLabel,
  nextLabel,
  onClose,
  listPanel,
  footerStart,
  footerEnd,
  scrollResetKey,
  children,
}: Props) => {
  const { t } = useTranslation();
  const root = useRef<HTMLDivElement>(null);
  const body = useRef<HTMLDivElement>(null);

  useHiddenIntercomLauncher();

  useEffect(() => {
    root.current?.focus();
  }, []);

  useEffect(() => {
    if (body.current) body.current.scrollTop = 0;
  }, [scrollResetKey]);

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      onClose();
      return;
    }
    if (isTextEntry(event.target) || !isExactlyAlt(event)) return;
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    // The overlay swallows Alt+arrow for as long as it is the active region, whether or not there is
    // a step to make: on Windows and Linux those are Back and Forward, and at the ends of the list —
    // exactly where the buyer presses one expecting nothing to happen — the browser would leave the
    // view instead, taking the unsaved work in it along.
    event.preventDefault();
    if (event.key === "ArrowLeft" && hasPrevious) onPrevious?.();
    if (event.key === "ArrowRight" && hasNext) onNext?.();
  };

  const previous = previousLabel ?? String(t("Previous"));
  const next = nextLabel ?? String(t("Next"));

  return (
    <Portal>
      <Flex
        ref={root}
        tabIndex={-1}
        outline="none"
        role="region"
        aria-label={String(t("Focus view"))}
        onKeyDown={onKeyDown}
        position="fixed"
        inset="0"
        zIndex="overlay"
        direction="column"
        bg="smBackground"
      >
        <Flex
          as="header"
          flexShrink={0}
          align="center"
          gap="4"
          px={{ base: "4", md: "6" }}
          py="3"
          borderBottomWidth="1px"
          borderColor="smBorder"
        >
          <Box minW="0" flex="1">
            <Flex align="baseline" gap="2" flexWrap="wrap">
              {eyebrow && (
                <Text fontSize="md" fontWeight="bold" color="smText">
                  {eyebrow}
                </Text>
              )}
              <Text fontSize="md" color="smMuted" noOfLines={1}>
                {title}
              </Text>
            </Flex>
            {subtitle && (
              <Text fontSize="xs" color="smMuted" noOfLines={1}>
                {subtitle}
              </Text>
            )}
          </Box>

          {position && (
            <HStack spacing="2" flexShrink={0}>
              <IconButton
                aria-label={previous}
                title={`${altKeyLabel()} ←`}
                aria-keyshortcuts="Alt+ArrowLeft"
                icon={<FiChevronLeft />}
                size="sm"
                variant="outline"
                onClick={onPrevious}
                isDisabled={!hasPrevious}
              />
              <Text fontSize="sm" fontWeight="semibold" color="smText" whiteSpace="nowrap">
                {t("{{current}} of {{total}}", position)}
              </Text>
              <IconButton
                aria-label={next}
                title={`${altKeyLabel()} →`}
                aria-keyshortcuts="Alt+ArrowRight"
                icon={<FiChevronRight />}
                size="sm"
                variant="outline"
                onClick={onNext}
                isDisabled={!hasNext}
              />
            </HStack>
          )}

          <Button flexShrink={0} variant="outline" size="sm" rightIcon={<FiX />} onClick={onClose}>
            {t("Close")}
          </Button>
        </Flex>

        <Flex flex="1" minH="0" overflow="hidden">
          {listPanel && (
            <Box
              as="aside"
              display={{ base: "none", lg: "block" }}
              w="288px"
              flexShrink={0}
              minH="0"
              borderRightWidth="1px"
              borderColor="smBorder"
              bg="smBackground"
            >
              {listPanel}
            </Box>
          )}
          <Box
            ref={body}
            flex="1"
            minH="0"
            overflowY="auto"
            overscrollBehavior="contain"
            tabIndex={-1}
            outline="none"
            bg="smBackgroundSecondary"
            p={{ base: "4", md: "6", lg: "8" }}
          >
            {children}
          </Box>
        </Flex>

        <Flex
          as="footer"
          flexShrink={0}
          align="center"
          justify="space-between"
          gap="4"
          px={{ base: "4", md: "6" }}
          py="4"
          borderTopWidth="1px"
          borderColor="smBorder"
          bg="smBackground"
        >
          <HStack spacing="3">{footerStart}</HStack>
          <HStack spacing="3">{footerEnd}</HStack>
        </Flex>
      </Flex>
    </Portal>
  );
};
